/**
 * Settings Service
 * Handles system settings using Supabase backend
 */
import supabaseService from './supabaseService';
import logger from '../utils/logger';

const SETTINGS_TABLE = 'settings';
const SETTINGS_ROW_ID = 'system';

const DEFAULT_SETTINGS = {
  yearlyTokenLimit: 12,
  maxConsecutiveDays: 3,
  maxConsecutiveDaysPerMonth: 3,
  minAdvanceBookingDays: 0,
  minAdvanceNoticeDays: 14,
  maxAdvanceBookingDays: 365,
  allowWeekendRequests: false,
  emailNotificationsEnabled: true,
  systemName: 'IDT Ignite Quarks Platform',
  systemVersion: '1.0.0',
};

class SettingsService {
  async getSettings() {
    try {
      logger.info('Fetching settings', { operation: 'getSettings' });

      const settings = await supabaseService.select(SETTINGS_TABLE, {
        filters: [{ column: 'id', value: SETTINGS_ROW_ID }],
        single: true,
      });

      if (!settings) {
        logger.warning('Settings not found in Supabase, returning defaults', {
          operation: 'getSettings',
        });
        return { ...DEFAULT_SETTINGS };
      }

      const mergedSettings = {
        ...DEFAULT_SETTINGS,
        ...settings,
      };

      logger.info('Settings fetched successfully', {
        operation: 'getSettings',
        keys: Object.keys(mergedSettings),
      });

      return mergedSettings;
    } catch (error) {
      logger.error('Error fetching settings', error, { operation: 'getSettings' });
      return { ...DEFAULT_SETTINGS };
    }
  }

  async getSetting(key) {
    try {
      logger.info('Fetching setting', { key, operation: 'getSetting' });
      const settings = await this.getSettings();
      const value = settings[key];

      if (value === undefined) {
        logger.warning('Setting not found', { key, operation: 'getSetting' });
        return null;
      }

      return value;
    } catch (error) {
      logger.error('Error fetching setting', error, { key, operation: 'getSetting' });
      throw error;
    }
  }

  async updateSettings(updates) {
    try {
      logger.info('Updating settings', { updates, operation: 'updateSettings' });

      const currentSettings = await this.getSettings();
      const timestamp = new Date().toISOString();

      const payload = {
        ...currentSettings,
        ...updates,
        id: SETTINGS_ROW_ID,
        updatedAt: timestamp,
      };

      if (!payload.createdAt) {
        payload.createdAt = timestamp;
      }

      const result = await supabaseService.upsert(SETTINGS_TABLE, payload, {
        onConflict: 'id',
        single: true,
      });

      const updatedSettings = Array.isArray(result) ? result[0] : result;
      const merged = {
        ...DEFAULT_SETTINGS,
        ...updatedSettings,
      };

      logger.info('Settings updated successfully', {
        operation: 'updateSettings',
        updatedKeys: Object.keys(updates),
      });

      return merged;
    } catch (error) {
      logger.error('Error updating settings', error, { operation: 'updateSettings' });
      throw error;
    }
  }

  async updateSetting(key, value) {
    try {
      logger.info('Updating single setting', { key, value, operation: 'updateSetting' });
      return await this.updateSettings({ [key]: value });
    } catch (error) {
      logger.error('Error updating setting', error, { key, operation: 'updateSetting' });
      throw error;
    }
  }

  async initializeSettings() {
    try {
      logger.info('Initializing settings', { operation: 'initializeSettings' });

      const existing = await supabaseService.select(SETTINGS_TABLE, {
        filters: [{ column: 'id', value: SETTINGS_ROW_ID }],
        single: true,
      });

      if (existing) {
        logger.info('Settings already initialized', { operation: 'initializeSettings' });
        return {
          ...DEFAULT_SETTINGS,
          ...existing,
        };
      }

      const timestamp = new Date().toISOString();
      const payload = {
        ...DEFAULT_SETTINGS,
        id: SETTINGS_ROW_ID,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      await supabaseService.insert(SETTINGS_TABLE, payload);

      logger.info('Settings initialized successfully', { operation: 'initializeSettings' });
      return payload;
    } catch (error) {
      logger.error('Error initializing settings', error, { operation: 'initializeSettings' });
      throw error;
    }
  }
}

const settingsService = new SettingsService();
export default settingsService;

